import React, { useCallback, useEffect, useRef, useState } from 'react';

import { get, post } from '@rails/request.js';

import { getDesktopBridge, getDesktopOauthCapabilities, openDesktopOauth } from '../../lib/desktopOauth';
import { logError } from '../../lib/logger';
import { getMetaContent, rootDomainUrl } from '../../lib/meta';

import GmailIcon from '../../../assets/images/gmail-icon.svg';

import useDesktopWindowFocusSignal from '../../hooks/useDesktopWindowFocusSignal';
import AuthButton from '../auth/AuthButton';
import { AuthBackLink, AuthFormMessage, AuthHeaderBlock, AuthPageStack } from '../auth/AuthFlow';
import AuthLayout from '../auth/AuthLayout';

const POLL_INTERVAL_MS = 1500;
const DEFAULT_RETURN_PATH = '/app/start';
const IMPORTING_STATUSES = new Set(['pending', 'importing']);
const FORM_MESSAGE_ID = 'contacts-setup-message';

function isImporting(contactImport) {
  return IMPORTING_STATUSES.has(contactImport?.status);
}

function isSucceeded(contactImport) {
  return contactImport?.status === 'succeeded';
}

function isSkipped(contactImport) {
  return contactImport?.status === 'skipped';
}

function isFailed(contactImport) {
  return contactImport?.status === 'failed';
}

function Spinner() {
  return <span className="size-10 animate-spin rounded-full border-2 border-gray-200 border-t-idle-blue" aria-hidden="true" />;
}

export default function ContactsSetupApp({ initialImport, returnUrl }) {
  const desktopBridge = getDesktopBridge();
  const csrfToken = getMetaContent('csrf-token') || '';
  const [contactImport, setContactImport] = useState(initialImport);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [error, setError] = useState(null);
  const pollingRef = useRef(false);
  const desktopFocusSignal = useDesktopWindowFocusSignal();

  const resolvedReturnUrl = returnUrl || rootDomainUrl(DEFAULT_RETURN_PATH);
  const busy = isAuthorizing || isImporting(contactImport);

  const refreshStatus = useCallback(async function refreshContactsStatus() {
    if (pollingRef.current) return null;

    pollingRef.current = true;
    try {
      const response = await get('/contacts/setup/status', { responseKind: 'json' });
      const data = await response.json;
      if (response.ok && data.ok) {
        const latestImport = data.data?.import || null;
        setContactImport(latestImport);
        return latestImport;
      }
    } catch (statusError) {
      logError('ContactsSetupApp', 'Failed to refresh contacts import status', { error: statusError });
    } finally {
      pollingRef.current = false;
    }

    return null;
  }, []);

  useEffect(
    function reenableAuthorizeOnDesktopReturn() {
      if (!isAuthorizing || !desktopFocusSignal) return;

      refreshStatus().then(function handleLatestImport(latestImport) {
        if (latestImport && !isImporting(latestImport)) setIsAuthorizing(false);
      });
    },
    [desktopFocusSignal, isAuthorizing, refreshStatus],
  );

  useEffect(
    function clearAuthorizingWhenImportExists() {
      if (contactImport) setIsAuthorizing(false);
    },
    [contactImport],
  );

  useEffect(
    function pollContactsImportStatus() {
      if (!busy) return undefined;

      refreshStatus();
      const timer = window.setInterval(refreshStatus, POLL_INTERVAL_MS);
      return function pollContactsImportStatusCleanup() {
        window.clearInterval(timer);
      };
    },
    [busy, refreshStatus],
  );

  useEffect(
    function redirectAfterSuccessfulImport() {
      if (!isSucceeded(contactImport) && !isSkipped(contactImport)) return undefined;

      const timer = window.setTimeout(function redirectToReturnUrl() {
        window.location.assign(resolvedReturnUrl);
      }, 700);

      return function redirectAfterSuccessfulImportCleanup() {
        window.clearTimeout(timer);
      };
    },
    [contactImport, resolvedReturnUrl],
  );

  const handleDesktopAuthorize = useCallback(
    async function handleDesktopAuthorizeCallback() {
      if (!desktopBridge || isAuthorizing) return;

      setIsAuthorizing(true);
      setError(null);

      try {
        const capabilities = await getDesktopOauthCapabilities(desktopBridge);
        const response = await post('/contacts/setup/google', {
          body: {
            client: 'desktop',
            completion_transport: capabilities.authenticationSession && capabilities.httpsCallbacks ? 'authentication_session' : undefined,
          },
          responseKind: 'json',
        });
        const data = await response.json;
        const startUrl = data.data?.start_url;
        const callbackUrl = data.data?.callback_url;

        if (!response.ok || !data.ok || !startUrl) throw new Error('failed to start contacts oauth');

        const desktopOauth = await openDesktopOauth({ bridge: desktopBridge, startUrl, callbackUrl, capabilities });
        if (desktopOauth?.completed) {
          const latestImport = await refreshStatus();
          if (!desktopOauth.succeeded || !latestImport) setIsAuthorizing(false);
        }
      } catch (authorizeError) {
        logError('ContactsSetupApp', 'Failed to start desktop Google contacts OAuth', { error: authorizeError });
        setError('Could not start Google contacts access. Please try again.');
        setIsAuthorizing(false);
      }
    },
    [desktopBridge, isAuthorizing, refreshStatus],
  );

  const handleSkip = useCallback(
    async function handleSkipCallback() {
      if (isSkipping) return;

      setIsSkipping(true);
      setError(null);

      try {
        const response = await post('/contacts/setup/skip', { responseKind: 'json' });
        const data = await response.json;
        if (!response.ok || !data.ok) throw new Error('failed to skip contacts setup');

        window.location.assign(data.data?.redirect_url || resolvedReturnUrl);
      } catch (skipError) {
        logError('ContactsSetupApp', 'Failed to skip contacts setup', { error: skipError });
        setError('Could not continue. Please try again.');
        setIsSkipping(false);
      }
    },
    [isSkipping, resolvedReturnUrl],
  );

  const showProgress = busy || isSucceeded(contactImport) || isSkipped(contactImport);
  const progressDone = isSucceeded(contactImport) || isSkipped(contactImport);
  const progressTitle = isSucceeded(contactImport) ? 'Contacts imported' : isSkipped(contactImport) ? 'Continuing' : 'Importing contacts';
  const progressBody = progressDone ? 'Taking you to Idle.' : 'This usually takes a few seconds.';
  const failedMessage = isFailed(contactImport) ? 'Could not import your contacts. Try again or skip for now.' : null;

  if (showProgress) {
    return (
      <AuthLayout>
        <AuthPageStack>
          <div className="flex w-full flex-col items-center gap-6">
            <Spinner />
            <AuthHeaderBlock title={progressTitle}>{progressBody}</AuthHeaderBlock>
          </div>
        </AuthPageStack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthPageStack>
        <div className="flex w-full flex-col items-center gap-6">
          <GmailIcon aria-hidden="true" className="h-[100px] w-auto" />
          <AuthHeaderBlock title="Find people you know">
            Sync your Gmail contacts with Idle to easily find and invite people you know
          </AuthHeaderBlock>
        </div>

        <div className="flex w-full flex-col gap-6">
          <AuthFormMessage error={error || failedMessage} id={FORM_MESSAGE_ID} />
          {desktopBridge ? (
            <AuthButton type="button" disabled={isAuthorizing || isSkipping} onClick={handleDesktopAuthorize}>
              {isAuthorizing ? 'Waiting for Google...' : 'Sync contacts'}
            </AuthButton>
          ) : (
            <form method="post" action="/contacts/setup/google" className="w-full">
              <input type="hidden" name="authenticity_token" value={csrfToken} readOnly />
              <AuthButton type="submit" disabled={isSkipping}>
                Sync contacts
              </AuthButton>
            </form>
          )}
          <AuthBackLink disabled={isAuthorizing || isSkipping} label={isSkipping ? 'Skipping...' : 'Skip for now'} onClick={handleSkip} />
        </div>
      </AuthPageStack>
    </AuthLayout>
  );
}
